// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast} from '@wandb/weave/common/components/elements/Toast';
import WandbLoader from '@wandb/weave/common/components/WandbLoader';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
// @todo: Migrate to 'SkipToContent' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Content, List, Root, Trigger} from '@wandb/weave/components/Tabs';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import copyToClipboard from 'copy-to-clipboard';
import React, {useCallback, useEffect, useState} from 'react';
import {ExecutionResult} from 'react-apollo';

import {TestGenericWebhookIntegrationMutation} from '../../generated/graphql';
import {Banner, BannerFlexWrapper} from '../Banner';
import {CodeEditor} from '../Launch/CodeEditor';
// eslint-disable-next-line import/no-cycle -- please fix if you can
import {useTestGenericWebhook} from './hooks';
import {TestWebhookCurl} from './TestWebhookCurl';
import {GenericWebhook} from './WebhooksTable';

type TestGenericWebhookResponseData = {
  ok: boolean;
  response: {
    // Based on backend implementation
    response: string;
    error: string;
  };
};

const EMPTY_RESPONSE: TestGenericWebhookResponseData = {
  ok: false,
  response: {
    response: '',
    error: '',
  },
};

const validJson = (data: string) => {
  try {
    JSON.parse(data);
    return true;
  } catch {
    return false;
  }
};

function parseTestGenericWebhookResponse(
  resp: ExecutionResult<TestGenericWebhookIntegrationMutation> | undefined
): TestGenericWebhookResponseData {
  if (resp == null) {
    return EMPTY_RESPONSE;
  }
  let respJson = EMPTY_RESPONSE;

  const response = resp?.data?.testGenericWebhookIntegration?.response ?? '';
  let responseInner = JSON.parse(response);
  if (validJson(responseInner.response)) {
    const jsonInner = JSON.parse(responseInner.response);
    responseInner = {...responseInner, response: jsonInner};
  }
  respJson = {
    ...respJson,
    ok: resp?.data?.testGenericWebhookIntegration?.ok ?? false,
    response: responseInner,
  };
  return respJson;
}

type TestWebhookDrawerInnerProps = {
  entityName: string;
  webhook: GenericWebhook;
};

export const TestWebhookDrawerInner = ({
  entityName,
  webhook,
}: TestWebhookDrawerInnerProps) => {
  const [tab, setTab] = useState('in-app');
  const [webhookPayload, setWebhookPayload] = useState('{\n\n\n\n\n}');
  const [webhookPayloadError, setWebhookPayloadError] = useState('');
  const onWebhookPayloadChange = (value: string) => {
    setWebhookPayload(value);
    setWebhookPayloadError('');
  };
  const {testGenericWebhook, isLoading: isTestLoading} =
    useTestGenericWebhook();
  const [isTestButtonDisabled, setIsTestButtonDisabled] = useState(false);
  const [testResponseData, setTestResponseData] = useState<
    TestGenericWebhookResponseData | undefined
  >(undefined);
  const [testResponseDataStr, setTestResponseDataStr] = useState('');
  useEffect(() => {
    setIsTestButtonDisabled(
      (entityName === '' ||
        webhook.urlEndpoint === '' ||
        webhookPayloadError !== '') ??
        false
    );
  }, [entityName, webhook.urlEndpoint, webhookPayloadError]);

  useEffect(() => {
    if (testResponseData != null) {
      setTestResponseDataStr(
        JSON.stringify(testResponseData.response, null, 4)
      );
    }
  }, [testResponseData]);

  const onCopyResponseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    copyToClipboard(testResponseDataStr);
    toast('URL copied to clipboard');
  };

  // Validate json payload
  const validate = useCallback(
    (overridesToValidate: string) => {
      let ok = true;
      try {
        JSON.parse(overridesToValidate);
        setWebhookPayloadError('');
      } catch (err) {
        setWebhookPayloadError('Payload must be valid JSON');
        ok = false;
      }
      return ok;
    },
    [setWebhookPayloadError]
  );
  useEffect(() => {
    validate(webhookPayload);
  }, [webhookPayload, validate]);
  return (
    <Tailwind style={{width: '100%', height: '100%'}}>
      <div className="self-start"></div>
      <Root value={tab} onValueChange={d => setTab(d)}>
        <List>
          <Trigger value="in-app">In app testing</Trigger>
          <Trigger value="curl">cURL command format</Trigger>
        </List>
        <Content value="in-app">
          <div className="mt-8 flex flex-col gap-8 text-base">
            <div className="font-semibold leading-8">Test with a payload</div>
            Test that your webhook URL is correctly configured by clicking "Test
            webhook" button below. This will send a POST request and show the
            server's response.
            <CodeEditor
              value={webhookPayload}
              language="json"
              onChange={onWebhookPayloadChange}
            />
            {webhookPayloadError && (
              <div className="text-sm text-red-500">{webhookPayloadError}</div>
            )}
            <Button
              className="mt-8"
              size="medium"
              disabled={isTestButtonDisabled}
              onClick={async () => {
                const resp = await testGenericWebhook(
                  entityName,
                  webhook.urlEndpoint.trim(),
                  webhookPayload,
                  webhook.accessTokenRef,
                  webhook.secretRef
                );
                const respData = parseTestGenericWebhookResponse(resp);
                setTestResponseData(respData);
              }}>
              Test webhook
            </Button>
            {isTestLoading && (
              <div className="mt-16 inline-flex items-center">
                <WandbLoader
                  inline
                  active
                  name="webhook-test-loading"
                  size="tiny"
                />
                <div className="ml-10 inline-flex">
                  Awaiting server response
                </div>
              </div>
            )}
            {!isTestLoading && testResponseData != null && (
              <>
                {testResponseData?.ok === true && (
                  <Banner className="mt-8 bg-green-300 bg-opacity-[.24] text-green-600">
                    <BannerFlexWrapper>
                      <Icon name="checkmark" style={{marginRight: 'auto'}} />
                      <div className="inline">
                        <span className="font-semibold">Success</span>
                        <span className="ml-4 inline">
                          The test request was successfully processed by the
                          server
                        </span>
                      </div>
                    </BannerFlexWrapper>
                  </Banner>
                )}
                {testResponseData?.ok === false && (
                  <Banner variant="error">
                    <BannerFlexWrapper>
                      <Icon name="warning-alt" style={{marginRight: 'auto'}} />
                      <div className="inline">
                        <span className="font-semibold">Failure</span>
                        <span className="ml-4 inline">
                          The test request resulted in an invalid status code
                        </span>
                      </div>
                    </BannerFlexWrapper>
                  </Banner>
                )}
                <div className="mt-24 rounded bg-moon-50 p-1">
                  <div className="mx-12 my-8 flex flex-row text-moon-800">
                    <div className="whitespace-break-spaces break-all font-['Source_Code_Pro'] text-sm leading-6">
                      {testResponseDataStr}
                    </div>
                    <div className="ml-auto mt-4">
                      <Button
                        size="small"
                        variant="secondary"
                        startIcon="copy"
                        onClick={onCopyResponseClick}>
                        Copy
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Content>
        <Content value="curl">
          <TestWebhookCurl webhook={webhook} requestPayload={webhookPayload} />
        </Content>
      </Root>
    </Tailwind>
  );
};
